import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  TextInput,
  Select,
  Button,
  Group,
  Badge,
  Pagination,
  Stack,
  Text,
  Loader,
  Alert,
  ActionIcon,
  Modal,
  Title,
  NumberInput,
  Textarea
} from '@mantine/core';
import {
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
  IconPlus,
  IconExclamationCircle,
  IconRefresh
} from '@tabler/icons-react';
import { api } from '../../services/api';
import ProductForm from './ProductForm';

const ProductManagement = ({ sellerProfile, canSell }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modals
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.getSellerProducts(Object.fromEntries(params));
      setProducts(response.products);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchProducts();
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const openCreateModal = () => {
    setSelectedProduct(null);
    setIsEditing(false);
    setProductModalOpen(true);
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setIsEditing(true);
    setProductModalOpen(true);
  };

  const handleProductSaved = () => {
    setProductModalOpen(false);
    setSelectedProduct(null);
    fetchProducts();
  };

  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      setDeleting(true);
      await api.deleteProduct(selectedProduct.id);
      setDeleteModalOpen(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err.response?.data?.error || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'draft': return 'gray';
      case 'inactive': return 'yellow';
      case 'out_of_stock': return 'red';
      case 'discontinued': return 'dark';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && products.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <Stack gap="md">
      {/* Controls */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Group align="center" gap="md">
            <TextInput
              placeholder="Search products..."
              leftSection={<IconSearch size="0.9rem" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              data={[
                { value: '', label: 'All Statuses' },
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'out_of_stock', label: 'Out of Stock' },
                { value: 'discontinued', label: 'Discontinued' }
              ]}
            />
            <Button onClick={handleSearch} loading={loading}>
              Search
            </Button>
            <ActionIcon onClick={fetchProducts} variant="light">
              <IconRefresh size="1rem" />
            </ActionIcon>
          </Group>

          <Group>
            <Button
              onClick={openCreateModal}
              leftSection={<IconPlus size="1rem" />}
              disabled={!canSell}
            >
              Add Product
            </Button>
            {!canSell && (
              <Text size="sm" c="dimmed">
                You need to be an approved seller to add products
              </Text>
            )}
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error}
            </Alert>
          )}
        </Stack>
      </Card>

      {/* Products Table */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Quantity</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Views</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {products.map((product) => (
              <Table.Tr key={product.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Text fw={500}>{product.title}</Text>
                    <Text size="xs" c="dimmed">{product.condition}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text fw={500}>${product.price}</Text>
                </Table.Td>
                <Table.Td>
                  <Text color={product.quantity === 0 ? 'red' : undefined}>
                    {product.quantity}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusBadgeColor(product.status)} variant="light">
                    {product.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{product.views}</Table.Td>
                <Table.Td>{formatDate(product.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon 
                      variant="light" 
                      onClick={() => openEditModal(product)}
                    >
                      <IconEdit size="1rem" />
                    </ActionIcon>
                    <ActionIcon 
                      variant="light" 
                      color="red"
                      onClick={() => openDeleteModal(product)}
                    >
                      <IconTrash size="1rem" />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {products.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No products found</Text>
            {canSell && (
              <Button 
                onClick={openCreateModal} 
                leftSection={<IconPlus size="1rem" />}
                mt="md"
                variant="light"
              >
                Create your first product
              </Button>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Card>

      {/* Product Form Modal */}
      <Modal
        opened={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={<Title order={3}>{isEditing ? 'Edit Product' : 'Add Product'}</Title>}
        size="lg"
      >
        <ProductForm
          product={selectedProduct}
          isEditing={isEditing}
          onSave={handleProductSaved}
          onCancel={() => setProductModalOpen(false)}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={<Title order={3}>Delete Product</Title>}
        size="md"
      >
        {selectedProduct && (
          <Stack gap="md">
            <Text>
              Are you sure you want to delete "{selectedProduct.title}"? This action cannot be undone.
            </Text>
            <Group align="right">
              <Button
                variant="light"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteProduct}
                loading={deleting}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default ProductManagement;